import React, { useState } from "react";

import { IDog, IDogRun } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import { Modal } from "../../../components/Modal/Modal";
import FastCATRunForm from "./FastCATRunForm";
import NoseworkRunForm from "./NoseworkRunForm";
import ScentworkRunForm from "./ScentworkRunForm";
import FetchRunForm from "./FetchRunForm";
import ObedienceRunForm from "./ObedienceRunForm";
import RallyRunForm from "./RallyRunForm";
import UKCObedienceRunForm from "./UKCObedienceRunForm";
import UKCRallyRunForm from "./UKCRallyRunForm";
import BarnHuntRunForm from "./BarnHuntRunForm";
import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";

import "./ManualRunEntryModal.scss";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  dog: IDog;
  sportType: string; // show_type value
  sanctioningClub: string;
  onSuccess: (createdRun?: any) => void;
  editRun?: IDogRun; // If provided, we're editing instead of creating
}

const ManualRunEntryModal: React.FC<IProps> = ({
  isOpen,
  onClose,
  dog,
  sportType,
  sanctioningClub,
  onSuccess,
  editRun
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (formData: any) => {
    setIsSubmitting(true);
    let createdRun: any;

    try {
      if (editRun) {
        // Update existing run. Use the run being edited as the id source — not
        // every form echoes run_id back (Scentwork didn't), which silently broke edits.
        const runId = editRun._id;
        if (sportType === Constants.show_type.fast_cat) {
          await apiService.updateManualFastCATRun(runId, {
            time_seconds: formData.time_seconds,
            trial_date: formData.trial_date,
            event_name: formData.event_name,
            location: formData.location,
            notes: formData.notes
          });
        } else if (sportType === Constants.show_type.nosework) {
          await apiService.updateManualNoseworkRun(runId, {
            level: formData.level,
            element: formData.element,
            division: formData.division,
            result_status: formData.result_status,
            trial_date: formData.trial_date,
            event_name: formData.event_name,
            location: formData.location,
            notes: formData.notes
          });
        } else if (sportType === Constants.show_type.scentwork) {
          // ScentworkRunForm emits camelCase (matching its create endpoint);
          // the update endpoint expects snake_case.
          await apiService.updateManualScentworkRun(runId, {
            level: formData.level,
            element: formData.element,
            result_status: formData.resultStatus ?? formData.result_status,
            trial_date: formData.trialDate ?? formData.trial_date,
            event_name: formData.eventName ?? formData.event_name,
            location: formData.location,
            notes: formData.notes
          });
        } else if (sportType === Constants.show_type.fetch) {
          await apiService.updateManualFetchRun(runId, {
            level: formData.level,
            resultStatus: formData.resultStatus,
            trialDate: formData.trialDate,
            judgeNumber: formData.judgeNumber,
            judgeName: formData.judgeName,
            eventName: formData.eventName,
            location: formData.location,
            notes: formData.notes
          });
        } else if (sportType === Constants.show_type.obedience) {
          await apiService.updateManualObedienceRun(runId, {
            obedienceClass: formData.obedienceClass,
            score: formData.score,
            resultStatus: formData.resultStatus,
            trialDate: formData.trialDate,
            judgeNumber: formData.judgeNumber,
            judgeName: formData.judgeName,
            eventName: formData.eventName,
            location: formData.location,
            notes: formData.notes
          });
        } else if (sportType === Constants.show_type.rally_obedience) {
          await apiService.updateManualRallyRun(runId, {
            rallyClass: formData.rallyClass,
            score: formData.score,
            resultStatus: formData.resultStatus,
            trialDate: formData.trialDate,
            judgeNumber: formData.judgeNumber,
            judgeName: formData.judgeName,
            eventName: formData.eventName,
            location: formData.location,
            notes: formData.notes
          });
        } else if (sportType === Constants.show_type.ukc_obedience) {
          await apiService.updateManualUKCObedienceRun(runId, {
            obedienceClass: formData.obedienceClass,
            score: formData.score,
            resultStatus: formData.resultStatus,
            trialDate: formData.trialDate,
            placement: formData.placement,
            dogsDefeated: formData.dogsDefeated,
            awards: formData.awards,
            judgeNumber: formData.judgeNumber,
            judgeName: formData.judgeName,
            eventName: formData.eventName,
            location: formData.location,
            notes: formData.notes
          });
        } else if (sportType === Constants.show_type.ukc_rally) {
          await apiService.updateManualUKCRallyRun(runId, {
            rallyClass: formData.rallyClass,
            score: formData.score,
            resultStatus: formData.resultStatus,
            trialDate: formData.trialDate,
            placement: formData.placement,
            dogsDefeated: formData.dogsDefeated,
            awards: formData.awards,
            judgeNumber: formData.judgeNumber,
            judgeName: formData.judgeName,
            eventName: formData.eventName,
            location: formData.location,
            notes: formData.notes
          });
        } else if (sportType === Constants.show_type.barn_hunt) {
          await apiService.updateManualBarnHuntRun(runId, {
            level: formData.level,
            section: formData.section,
            resultStatus: formData.resultStatus,
            time: formData.time,
            trialDate: formData.trialDate,
            judgeNumber: formData.judgeNumber,
            judgeName: formData.judgeName,
            eventName: formData.eventName,
            location: formData.location,
            notes: formData.notes
          });
        }
        
        toast.show({
          title: "Run Updated",
          content: "Your run has been updated and title progress has been recalculated.",
          duration: 5000,
          type: "success"
        });
      } else {
        // Create new run
        if (sportType === Constants.show_type.fast_cat) {
          createdRun = await apiService.createManualFastCATRun(formData);
        } else if (sportType === Constants.show_type.nosework) {
          createdRun = await apiService.createManualNoseworkRun(formData);
        } else if (sportType === Constants.show_type.scentwork) {
          createdRun = await apiService.createManualScentworkRun(formData);
        } else if (sportType === Constants.show_type.fetch) {
          createdRun = await apiService.createManualFetchRun(formData);
        } else if (sportType === Constants.show_type.obedience) {
          createdRun = await apiService.createManualObedienceRun(formData);
        } else if (sportType === Constants.show_type.rally_obedience) {
          createdRun = await apiService.createManualRallyRun(formData);
        } else if (sportType === Constants.show_type.ukc_obedience) {
          createdRun = await apiService.createManualUKCObedienceRun(formData);
        } else if (sportType === Constants.show_type.ukc_rally) {
          createdRun = await apiService.createManualUKCRallyRun(formData);
        } else if (sportType === Constants.show_type.barn_hunt) {
          createdRun = await apiService.createManualBarnHuntRun(formData);
        }

        toast.show({
          title: "Run Added",
          content: "Your run has been saved and title progress has been recalculated.",
          duration: 5000,
          type: "success"
        });
      }

      onSuccess(createdRun);
      onClose();
    } catch (error: any) {
      console.error('Error saving run:', error);
      toast.show({
        title: editRun ? "Update Failed" : "Save Failed",
        content: "Unable to save the run. Please try again.",
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const renderSportForm = () => {
    // Use editRun._id as key when editing, or 'new' when creating to force remount
    const formKey = editRun?._id || 'new-' + Date.now();
    
    // Render appropriate form based on sport type
    switch (sportType) {
      case Constants.show_type.fast_cat:
        return (
          <FastCATRunForm
            key={formKey}
            dog={dog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            editRun={editRun}
          />
        );
      
      case Constants.show_type.nosework:
        return (
          <NoseworkRunForm
            dog={dog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            editRun={editRun}
          />
        );
      
      case Constants.show_type.scentwork:
        return (
          <ScentworkRunForm
            dog={dog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            editRun={editRun}
          />
        );
      
      case Constants.show_type.fetch:
        return (
          <FetchRunForm
            dog={dog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            editRun={editRun}
          />
        );

      case Constants.show_type.obedience:
        return (
          <ObedienceRunForm
            dog={dog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            editRun={editRun}
          />
        );
      
      case Constants.show_type.rally_obedience:
        return (
          <RallyRunForm
            dog={dog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            editRun={editRun}
          />
        );

      case Constants.show_type.ukc_obedience:
        return (
          <UKCObedienceRunForm
            dog={dog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            editRun={editRun}
          />
        );
      
      case Constants.show_type.ukc_rally:
        return (
          <UKCRallyRunForm
            dog={dog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            editRun={editRun}
          />
        );

      case Constants.show_type.barn_hunt:
        return (
          <BarnHuntRunForm
            dog={dog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            editRun={editRun}
          />
        );

      default:
        return (
          <div className="unsupportedSport">
            <p>Manual entry for {sportType} is not yet supported.</p>
            <Button variant="secondary" onClick={handleCancel}>Close</Button>
          </div>
        );
    }
  };

  return (
    <Modal
      isShown={isOpen}
      hide={handleCancel}
      className="manualRunEntryModal"
      modalContent={
        <div className="modalContentWrapper">
          <div className="modalHeader">
            <h2>{editRun ? "Edit Run" : "Add Manual Run"}</h2>
          </div>
          <div className="modalScrollableContent">
            {renderSportForm()}
          </div>
        </div>
      }
    />
  );
};

export default ManualRunEntryModal;

